import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyPipe, DatePipe } from '@angular/common';
import {
    ButtonComponent,
    CheckboxComponent,
    InlineNotificationComponent,
    LabelComponent,
    RootDirective,
} from '@pure/hive';
import { smartTimer } from '@pstg/smart-timer';
import moment from 'moment';
import { Subject, take } from 'rxjs';
import { PRODUCT_NAMES } from '../../../../constants/product-names';
import { ERROR_MESSAGES } from '../../../../constants/error-messages';
import { ToastService, ToastType } from '../../../../services/toast.service';
import {
    EvercycleBuybackRenderInfo,
    EvercycleOption,
    RECOMMENDATION_ACTION_EVENT_TOKEN,
    RecommendationActionEvent,
} from '../../../../data/models/recommendations.models';
import { EVERCYCLE_BUYBACK_DIALOG_PLUGIN_NAME } from './evercycle-plugin-names';
import { registerAsDialogPlugin } from '../../services/recommendation-component-registry.service';
import { emitEvercycleEvent, EVERCYCLE_EVENTS, EvercycleOfferAcceptedPayload } from './evercycle-amplitude';
import { EVERCYCLE_ACCEPT_AUDIENCE, EvercycleAcceptOutcome, EvercycleAcceptService } from './evercycle-accept.service';
import {
    EVERCYCLE_PAYMENT_TERMS,
    EVERCYCLE_PENDING_LABEL,
    EVERCYCLE_PROGRAM_LABEL,
    EVERCYCLE_VALUATION_CAPTION,
    EVERCYCLE_VALUATION_QUALIFIER,
    exclusionReasonCopy,
} from './evercycle-copy';
import { EvercycleFollowupDaysService } from './evercycle-followup-days.service';

export { EVERCYCLE_BUYBACK_DIALOG_PLUGIN_NAME };

/** Clock-poll interval; same cadence as the card plugin. */
export const EVERCYCLE_DIALOG_CLOCK_TICK_MS = 30_000;

/** View-model state for the Accept Valuation action. */
export type EvercycleAcceptStatus = 'idle' | 'in-flight' | 'accepted' | 'disabled-pending-refresh';

@Component({
    selector: 'evercycle-buyback-dialog-plugin',
    templateUrl: './evercycle-buyback-dialog-plugin.component.html',
    styleUrls: ['./evercycle-buyback-dialog-plugin.component.scss'],
    standalone: true,
    imports: [
        ButtonComponent,
        CheckboxComponent,
        InlineNotificationComponent,
        LabelComponent,
        RootDirective,
        CurrencyPipe,
        DatePipe,
    ],
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvercycleBuybackDialogPluginComponent {
    renderInfo = input.required<EvercycleBuybackRenderInfo>();

    protected readonly programLabel = EVERCYCLE_PROGRAM_LABEL;
    protected readonly pendingLabel = EVERCYCLE_PENDING_LABEL;
    protected readonly valuationCaption = EVERCYCLE_VALUATION_CAPTION;
    protected readonly valuationQualifier = EVERCYCLE_VALUATION_QUALIFIER;
    protected readonly paymentTerms = EVERCYCLE_PAYMENT_TERMS;
    protected readonly companyName = PRODUCT_NAMES.COMPANY_NAME;
    protected readonly RecommendationActionEvent = RecommendationActionEvent;

    /** Ticking clock signal so `isActionable` becomes reactive at the valuation deadline. */
    private readonly nowMs = signal(Date.now());

    /** Agreement checkbox state. The Accept button is disabled until this is ticked. */
    protected readonly agreementChecked = signal(false);

    /** Accept-flow state, surfaces as the button-disabled + confirmation gates. */
    protected readonly acceptStatus = signal<EvercycleAcceptStatus>('idle');

    /** Transient confirmation / error copy shown next to the button. */
    protected readonly acceptMessage = signal<string>('');

    private readonly acceptService = inject(EvercycleAcceptService);
    private readonly followupDaysService = inject(EvercycleFollowupDaysService);
    private readonly toastService = inject(ToastService, { optional: true });

    private readonly recommendationActionEvent$ = inject<Subject<RecommendationActionEvent>>(
        RECOMMENDATION_ACTION_EVENT_TOKEN,
        { optional: true },
    );
    private readonly destroyRef = inject(DestroyRef);

    protected readonly isActionable = computed(() => {
        const info = this.renderInfo();
        return info.isValuationActionableAt(moment.utc(this.nowMs()));
    });

    protected readonly validUntilDate = computed(() => this.renderInfo().valuation?.validUntilUtc?.toDate());

    protected readonly exclusionLines = computed(() =>
        this.renderInfo().excludedComponents.map(c => {
            try {
                return { label: c.label, reasonCopy: exclusionReasonCopy(c.reason) };
            } catch (err) {
                // Runtime fallback: a new upstream reason code must not break
                // the whole dialog. Strict `exclusionReasonCopy` behavior is
                // still exercised by its own unit test, so adding a new code
                // without updating the copy table is still caught in CI.
                // eslint-disable-next-line no-console
                console.error('EverCycle: unknown exclusion reason, using generic copy', c.reason, err);
                return { label: c.label, reasonCopy: 'Not eligible for buyback' };
            }
        }),
    );

    protected readonly acceptDisabled = computed(() => {
        if (!this.isActionable()) {
            return true;
        }
        if (!this.agreementChecked()) {
            return true;
        }
        return this.acceptStatus() !== 'idle';
    });

    /**
     * Tracks every `renderInfo.id` already impressioned by this instance so
     * re-binding to a previously-seen render-info does not re-emit the event.
     */
    private readonly emittedForIds = new Set<string>();

    constructor() {
        // Project convention (no-restricted-globals + no-restricted-properties):
        // use `@pstg/smart-timer` instead of raw setInterval / setTimeout.
        smartTimer(EVERCYCLE_DIALOG_CLOCK_TICK_MS, EVERCYCLE_DIALOG_CLOCK_TICK_MS)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.nowMs.set(Date.now()));

        // Read the follow-up window while the user is still deciding, so the
        // confirmation copy is ready the moment the POST succeeds.
        this.followupDaysService.businessDays$().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe();

        // Impression (dialog-opened) is deduped per unique renderInfo id so the
        // event fires once per dialog instance that reaches the user.
        effect(() => {
            const info = this.renderInfo();
            if (!info || this.emittedForIds.has(info.id)) {
                return;
            }
            this.emittedForIds.add(info.id);
            emitEvercycleEvent(EVERCYCLE_EVENTS.DIALOG_OPENED, {
                recommendation_id: info.recommendationId,
                program: info.program,
                org_id: info.orgId,
            });
        });

        // Accept-flow reset is keyed on the offer identity, not the render
        // info id: after a REFRESH on OFFER_MISMATCH / OFFER_EXPIRED, the
        // recommendation id stays the same but the valuation (and offer_id)
        // changes — the user then needs the Accept flow re-enabled so they
        // can accept the fresh offer.
        let lastSeenOfferId: string | null | undefined;
        effect(() => {
            const info = this.renderInfo();
            const currentOfferId = info?.valuation?.offerId ?? null;
            if (currentOfferId === lastSeenOfferId) {
                return;
            }
            lastSeenOfferId = currentOfferId;
            this.agreementChecked.set(false);
            this.acceptStatus.set('idle');
            this.acceptMessage.set('');
        });

        // One-shot timer at the actionable deadline so a dialog opened before
        // expiry flips to pending at the deadline rather than at the next poll.
        effect(() => {
            const info = this.renderInfo();
            const deadlineMoment = info.valuation?.actionableUntilUtc ?? info.valuation?.validUntilUtc;
            const expiryMs = deadlineMoment?.valueOf();
            if (!expiryMs) {
                return;
            }
            const delay = expiryMs - Date.now();
            if (delay <= 0 || delay > 2_147_483_647) {
                return;
            }
            smartTimer(delay + 1)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.nowMs.set(Date.now()));
        });
    }

    protected selectOption(option: EvercycleOption): void {
        const info = this.renderInfo();
        emitEvercycleEvent(EVERCYCLE_EVENTS.OPTION_SELECTED, {
            recommendation_id: info.recommendationId,
            program: info.program,
            org_id: info.orgId,
            option,
        });
        // The two Renew paths route into the container's existing renewal
        // flow (same event the contract-renewal dialog uses). The EverCycle
        // option has its own in-dialog follow-through (checkbox + Accept)
        // and does not need a relay here.
        if (option === 'RENEW_AS_IS' || option === 'RENEW_WITH_FOREVER') {
            this.handleActionEvent(RecommendationActionEvent.REQUEST_QUOTE);
        }
    }

    protected toggleAgreement(checked: boolean): void {
        this.agreementChecked.set(checked);
    }

    /**
     * Accept Valuation flow (spec 003/003). Guards:
     *   - Button is disabled until the agreement checkbox is checked.
     *   - On click we flip to `in-flight` immediately — a double-click fires
     *     one request because the second click sees `acceptDisabled() === true`
     *     and never reaches this method.
     *   - `info` and `valuation` are snapshotted here, not re-read from the
     *     signal inside `handleOutcome`. The container reuses this component
     *     across recommendations via `NgComponentOutlet`, so a slow response
     *     could otherwise land after `renderInfo()` has swapped to a different
     *     recommendation and misattribute the outcome.
     *   - The Amplitude `evercycle_offer_accepted` event is emitted only on
     *     a 200 outcome. Error paths never emit it.
     */
    protected accept(): void {
        if (this.acceptDisabled()) {
            return;
        }
        const info = this.renderInfo();
        const valuation = info.valuation;
        if (!valuation) {
            // Belt-and-braces: isActionable() already guards this.
            return;
        }

        this.acceptStatus.set('in-flight');

        this.acceptService
            .accept({
                recommendation_id: info.recommendationId,
                audience: EVERCYCLE_ACCEPT_AUDIENCE,
                status: 'OFFER_ACCEPTED',
                offer_id: valuation.offerId,
            })
            .subscribe(outcome => this.handleOutcome(outcome, info, valuation));
    }

    private handleOutcome(
        outcome: EvercycleAcceptOutcome,
        info: EvercycleBuybackRenderInfo,
        valuation: NonNullable<EvercycleBuybackRenderInfo['valuation']>,
    ): void {
        // Guard every UI-state mutation on "the dialog is still showing the
        // recommendation the request was posted for". The container reuses
        // this component across recommendations via NgComponentOutlet; a slow
        // response could land after the user has closed and reopened the
        // dialog on recommendation B. The Amplitude event is still attributed
        // to the snapshotted `info` (correct), but writing `acceptStatus` /
        // `acceptMessage` / `agreementChecked` would otherwise paint B's view
        // with A's outcome.
        const isCurrentDialog = this.renderInfo()?.id === info.id;
        switch (outcome.kind) {
            case 'ACCEPTED': {
                const payload: EvercycleOfferAcceptedPayload = {
                    recommendation_id: info.recommendationId,
                    program: info.program,
                    org_id: info.orgId,
                    offer_id: valuation.offerId,
                    total_amount_bucket: valuation.totalAmountBucket,
                };
                emitEvercycleEvent(EVERCYCLE_EVENTS.OFFER_ACCEPTED, payload);
                if (isCurrentDialog) {
                    this.acceptStatus.set('accepted');
                    this.showAcceptedMessage(info);
                }
                return;
            }
            case 'OFFER_MISMATCH': {
                this.toastService?.add(ToastType.info, 'The offer has been updated.');
                // Ask the container to refresh the recommendation so the
                // dialog re-binds to the new `offer_id`; keep the accept flow
                // DISABLED until that fresh feed arrives. Without this gate
                // the user could re-check the agreement and immediately
                // resubmit the same stale `offer_id`, which spog would
                // reject with another OFFER_MISMATCH, looping.
                this.handleActionEvent(RecommendationActionEvent.REFRESH);
                if (isCurrentDialog) {
                    this.acceptStatus.set('disabled-pending-refresh');
                    this.agreementChecked.set(false);
                    this.acceptMessage.set('Refreshing the offer…');
                }
                return;
            }
            case 'OFFER_EXPIRED': {
                this.toastService?.add(ToastType.info, 'This valuation has expired; a new one will be issued.');
                // Fall back to the pending state: clear the agreement, lock
                // the accept flow (so the stale valuation can't be
                // resubmitted), and ask the container to refresh the feed
                // so the next render-info replaces the stale valuation.
                this.handleActionEvent(RecommendationActionEvent.REFRESH);
                if (isCurrentDialog) {
                    this.agreementChecked.set(false);
                    this.acceptStatus.set('disabled-pending-refresh');
                    this.acceptMessage.set(this.pendingLabel);
                }
                return;
            }
            case 'NOT_OPEN':
            case 'NOT_ACTIVE': {
                // Dismissed or already-accepted elsewhere — refresh the feed
                // so the stale tile goes away. Only close the dialog if it is
                // still showing the recommendation we posted for; otherwise
                // the user has already moved on.
                this.handleActionEvent(RecommendationActionEvent.REFRESH);
                if (isCurrentDialog) {
                    this.handleActionEvent(RecommendationActionEvent.CANCEL);
                }
                return;
            }
            case 'UNAUTHORIZED': {
                if (!isCurrentDialog) return;
                this.acceptStatus.set('idle');
                this.acceptMessage.set("You don't have permission to accept this offer.");
                return;
            }
            case 'SERVER_ERROR': {
                this.toastService?.add(ToastType.error, ERROR_MESSAGES.GENERIC_RETRY);
                if (isCurrentDialog) {
                    this.acceptStatus.set('idle');
                    this.acceptMessage.set('');
                }
                return;
            }
        }
    }

    /**
     * The follow-up window is normally already resolved (it is read when the
     * dialog opens), in which case the full message is set synchronously. If
     * the read is still in flight, a thank-you line without a day count is
     * shown at once, so the user is not left without a confirmation while the
     * controls are already disabled, and it is replaced when the window lands.
     * The dialog may have moved on to another recommendation by then, so the
     * guard is re-checked.
     */
    private showAcceptedMessage(info: EvercycleBuybackRenderInfo): void {
        let resolved = false;
        this.followupDaysService
            .businessDays$()
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe(days => {
                resolved = true;
                if (this.renderInfo()?.id !== info.id) {
                    return;
                }
                this.acceptMessage.set(
                    `Thank you — the ${this.companyName} team will contact you within ${days} business days.`,
                );
            });
        if (!resolved) {
            this.acceptMessage.set(`Thank you — the ${this.companyName} team will contact you.`);
        }
    }

    handleActionEvent(action: RecommendationActionEvent): void {
        this.recommendationActionEvent$?.next(action);
    }
}

registerAsDialogPlugin(EVERCYCLE_BUYBACK_DIALOG_PLUGIN_NAME, EvercycleBuybackDialogPluginComponent);
